import React, { useState, useEffect } from 'react';
import '../CSS/AdminControls.css';
import { getBrandingValue, getCompanyName } from '../utils/subdomainUtils';

function AdminControls({ onLogout }) {
  const title = getBrandingValue('title');
  const companyName = getCompanyName();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7days');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock data - Replace with actual API calls
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAnalyticsData({
        overview: {
          totalRevenue: 15847.50,
          totalOrders: 342,
          totalCustomers: 198,
          avgOrderValue: 46.35,
          revenueChange: 12.5,
          ordersChange: 8.3,
          customersChange: 15.2,
          avgChange: 3.7
        },
        topQueries: [
          { query: "Something refreshing packed with vitamin C", count: 45, percentage: 13.2 },
          { query: "High protein smoothie for post-workout", count: 38, percentage: 11.1 },
          { query: "A sweet and fruity beverage", count: 32, percentage: 9.4 },
          { query: "Low sugar option under 150 calories", count: 28, percentage: 8.2 },
          { query: "Vegan-friendly options", count: 25, percentage: 7.3 }
        ],
        topProducts: [
          { name: "Strawberry Quick", orders: 67, revenue: 468.33, percentage: 19.6 },
          { name: "The Hulk", orders: 54, revenue: 377.46, percentage: 15.8 },
          { name: "Juicylicious", orders: 48, revenue: 335.52, percentage: 14.0 },
          { name: "Orange Juice", orders: 41, revenue: 286.59, percentage: 12.0 },
          { name: "Twisty Green", orders: 35, revenue: 244.65, percentage: 10.2 }
        ],
        recentOrders: [
          { id: "ORD-1047", phone: "(555) 123-4567", items: 3, total: 24.50, query: "Something healthy and green", time: "2 mins ago" },
          { id: "ORD-1046", phone: "(555) 234-5678", items: 2, total: 15.98, query: "Sweet berry combinations", time: "8 mins ago" },
          { id: "ORD-1045", phone: "N/A", items: 1, total: 6.99, query: "High protein option", time: "12 mins ago" },
          { id: "ORD-1044", phone: "(555) 345-6789", items: 4, total: 31.20, query: "Tropical fruit medley", time: "18 mins ago" },
          { id: "ORD-1043", phone: "(555) 456-7890", items: 2, total: 18.47, query: "Low calorie refreshing drink", time: "25 mins ago" }
        ],
        salesByHour: [
          { hour: "9AM", sales: 245 },
          { hour: "10AM", sales: 412 },
          { hour: "11AM", sales: 678 },
          { hour: "12PM", sales: 1234 },
          { hour: "1PM", sales: 1456 },
          { hour: "2PM", sales: 1123 },
          { hour: "3PM", sales: 890 },
          { hour: "4PM", sales: 1045 },
          { hour: "5PM", sales: 1312 },
          { hour: "6PM", sales: 978 }
        ],
        customerRetention: {
          returning: 142,
          new: 56,
          returningPercentage: 71.7
        }
      });
      setLoading(false);
    }, 500);
  }, [dateRange]);

  const renderOverviewTab = () => (
    <div className="dashboard-section">
      {/* Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon revenue-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <span className="metric-label">Total Revenue</span>
          </div>
          <div className="metric-value">${analyticsData.overview.totalRevenue.toLocaleString()}</div>
          <div className="metric-change positive">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
            +{analyticsData.overview.revenueChange}% from last period
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon orders-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </div>
            <span className="metric-label">Total Orders</span>
          </div>
          <div className="metric-value">{analyticsData.overview.totalOrders}</div>
          <div className="metric-change positive">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
            +{analyticsData.overview.ordersChange}% from last period
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon customers-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <span className="metric-label">Total Customers</span>
          </div>
          <div className="metric-value">{analyticsData.overview.totalCustomers}</div>
          <div className="metric-change positive">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
            +{analyticsData.overview.customersChange}% from last period
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon avg-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <span className="metric-label">Avg Order Value</span>
          </div>
          <div className="metric-value">${analyticsData.overview.avgOrderValue}</div>
          <div className="metric-change positive">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
            +{analyticsData.overview.avgChange}% from last period
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Sales by Hour</h3>
            <span className="chart-subtitle">Peak hours analysis</span>
          </div>
          <div className="bar-chart">
            {analyticsData.salesByHour.map((item, index) => {
              const maxSales = Math.max(...analyticsData.salesByHour.map(s => s.sales));
              const heightPercentage = (item.sales / maxSales) * 100;
              return (
                <div key={index} className="bar-item">
                  <div className="bar-wrapper">
                    <div 
                      className="bar-fill" 
                      style={{ height: `${heightPercentage}%` }}
                      title={`$${item.sales}`}
                    ></div>
                  </div>
                  <span className="bar-label">{item.hour}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Customer Retention</h3>
            <span className="chart-subtitle">New vs Returning customers</span>
          </div>
          <div className="retention-chart">
            <div className="donut-chart">
              <svg viewBox="0 0 100 100" className="donut-svg">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="12"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="var(--primary-color)"
                  strokeWidth="12"
                  strokeDasharray={`${analyticsData.customerRetention.returningPercentage * 2.513} 251.3`}
                  strokeDashoffset="62.825"
                  transform="rotate(-90 50 50)"
                  className="donut-segment"
                />
              </svg>
              <div className="donut-center">
                <div className="donut-percentage">{analyticsData.customerRetention.returningPercentage}%</div>
                <div className="donut-label">Returning</div>
              </div>
            </div>
            <div className="retention-stats">
              <div className="retention-stat">
                <div className="retention-stat-value">{analyticsData.customerRetention.returning}</div>
                <div className="retention-stat-label">
                  <span className="stat-dot returning"></span>
                  Returning Customers
                </div>
              </div>
              <div className="retention-stat">
                <div className="retention-stat-value">{analyticsData.customerRetention.new}</div>
                <div className="retention-stat-label">
                  <span className="stat-dot new"></span>
                  New Customers
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAIInsightsTab = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>AI Query Analytics</h2>
        <p>Customer preferences and search patterns</p>
      </div>

      <div className="insights-grid">
        <div className="insight-card full-width">
          <h3>Top Customer Queries</h3>
          <div className="queries-list">
            {analyticsData.topQueries.map((query, index) => (
              <div key={index} className="query-item">
                <div className="query-rank">{index + 1}</div>
                <div className="query-content">
                  <div className="query-text">{query.query}</div>
                  <div className="query-bar">
                    <div 
                      className="query-bar-fill" 
                      style={{ width: `${query.percentage * 4}%` }}
                    ></div>
                  </div>
                  <div className="query-stats">
                    <span className="query-count">{query.count} searches</span>
                    <span className="query-percentage">{query.percentage}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="insight-card">
          <h3>AI Recommendation Success Rate</h3>
          <div className="success-metric">
            <div className="success-circle">
              <svg viewBox="0 0 120 120" className="success-svg">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="8"/>
                <circle 
                  cx="60" 
                  cy="60" 
                  r="54" 
                  fill="none" 
                  stroke="var(--primary-color)" 
                  strokeWidth="8"
                  strokeDasharray="339.3"
                  strokeDashoffset="33.93"
                  transform="rotate(-90 60 60)"
                  className="success-circle-fill"
                />
              </svg>
              <div className="success-percentage">90%</div>
            </div>
            <p className="success-description">
              Customers who received AI recommendations completed their purchase
            </p>
          </div>
        </div>

        <div className="insight-card">
          <h3>Query Insights</h3>
          <div className="insight-stats">
            <div className="insight-stat-item">
              <div className="insight-stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </div>
              <div className="insight-stat-content">
                <div className="insight-stat-value">3.2</div>
                <div className="insight-stat-label">Avg searches per customer</div>
              </div>
            </div>
            <div className="insight-stat-item">
              <div className="insight-stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <div className="insight-stat-content">
                <div className="insight-stat-value">4.8</div>
                <div className="insight-stat-label">Avg recommendations per query</div>
              </div>
            </div>
            <div className="insight-stat-item">
              <div className="insight-stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                </svg>
              </div>
              <div className="insight-stat-content">
                <div className="insight-stat-value">85%</div>
                <div className="insight-stat-label">Customer satisfaction rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrdersTab = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>Recent Orders</h2>
        <p>Real-time order tracking and customer data</p>
      </div>

      <div className="orders-section">
        <div className="orders-table-card">
          <div className="table-header">
            <div className="table-filters">
              <button className="filter-btn active">All Orders</button>
              <button className="filter-btn">With Phone</button>
              <button className="filter-btn">Anonymous</button>
            </div>
            <button className="export-btn">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Export
            </button>
          </div>
          
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer Phone</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>AI Query</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.recentOrders.map((order, index) => (
                  <tr key={index}>
                    <td><span className="order-id">{order.id}</span></td>
                    <td>
                      {order.phone === "N/A" ? (
                        <span className="phone-anonymous">Anonymous</span>
                      ) : (
                        <span className="phone-number">{order.phone}</span>
                      )}
                    </td>
                    <td><span className="items-count">{order.items} items</span></td>
                    <td><span className="order-total">${order.total}</span></td>
                    <td><span className="order-query">{order.query}</span></td>
                    <td><span className="order-time">{order.time}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="top-products-card">
          <h3>Top Selling Products</h3>
          <div className="products-list">
            {analyticsData.topProducts.map((product, index) => (
              <div key={index} className="product-item">
                <div className="product-rank-badge">{index + 1}</div>
                <div className="product-details">
                  <div className="product-name">{product.name}</div>
                  <div className="product-stats">
                    <span>{product.orders} orders</span>
                    <span className="product-revenue">${product.revenue}</span>
                  </div>
                  <div className="product-bar">
                    <div 
                      className="product-bar-fill" 
                      style={{ width: `${product.percentage * 3}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderReportsTab = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>Reports & Export</h2>
        <p>Generate automated reports for your business</p>
      </div>

      <div className="reports-grid">
        <div className="report-card">
          <div className="report-icon monthly-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <h3>Monthly Sales Report</h3>
          <p>Comprehensive overview of monthly performance including revenue, orders, and trends</p>
          <button className="generate-report-btn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Generate Report
          </button>
        </div>

        <div className="report-card">
          <div className="report-icon customer-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <h3>Customer Insights Report</h3>
          <p>Detailed analysis of customer behavior, preferences, and retention metrics</p>
          <button className="generate-report-btn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Generate Report
          </button>
        </div>

        <div className="report-card">
          <div className="report-icon ai-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <h3>AI Performance Report</h3>
          <p>AI recommendation accuracy, popular queries, and conversion rates</p>
          <button className="generate-report-btn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Generate Report
          </button>
        </div>

        <div className="report-card">
          <div className="report-icon inventory-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <h3>Product Performance Report</h3>
          <p>Best sellers, revenue by product, and inventory recommendations</p>
          <button className="generate-report-btn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Generate Report
          </button>
        </div>
      </div>

      <div className="automated-reports-section">
        <h3>Automated Monthly Reports</h3>
        <p>Set up automatic report generation and email delivery</p>
        <div className="automation-card">
          <div className="automation-toggle">
            <label className="toggle-switch">
              <input type="checkbox" defaultChecked />
              <span className="toggle-slider"></span>
            </label>
            <div className="automation-info">
              <h4>Monthly Report Automation</h4>
              <p>Automatically generate and email comprehensive reports on the 1st of each month</p>
            </div>
          </div>
          <div className="automation-settings">
            <div className="setting-group">
              <label>Report Recipients</label>
              <input type="email" placeholder="manager@restaurant.com" />
            </div>
            <div className="setting-group">
              <label>Report Format</label>
              <select>
                <option>PDF Document</option>
                <option>Excel Spreadsheet</option>
                <option>Both PDF & Excel</option>
              </select>
            </div>
            <button className="save-settings-btn">Save Settings</button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-controls">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-controls">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1 className="admin-title">
            <svg 
              className="admin-icon" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
            Admin Dashboard
          </h1>
          <p className="admin-subtitle">{title} • {companyName}</p>
        </div>
        <div className="admin-header-actions">
          <select 
            className="date-range-select" 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="24hours">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="year">This Year</option>
          </select>
          <button className="logout-button" onClick={onLogout}>
            <svg 
              className="logout-icon" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Logout
          </button>
        </div>
      </div>

      <div className="admin-nav">
        <button 
          className={`admin-nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          Overview
        </button>
        <button 
          className={`admin-nav-btn ${activeTab === 'ai-insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai-insights')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
          AI Insights
        </button>
        <button 
          className={`admin-nav-btn ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
          Orders
        </button>
        <button 
          className={`admin-nav-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          Reports
        </button>
      </div>
      
      <div className="admin-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'ai-insights' && renderAIInsightsTab()}
        {activeTab === 'orders' && renderOrdersTab()}
        {activeTab === 'reports' && renderReportsTab()}
      </div>
    </div>
  );
}

export default AdminControls;

